/**
 * Receipt HTML Renderer — Client-side version of receipt-to-HTML conversion.
 *
 * Converts processed template JSON (from processReceiptTemplate) into an
 * HTML string suitable for Electron's native printDirect or printToPdf.
 * Runs in the renderer process.
 */

const PAPER_WIDTHS = {
  80: "80mm",
  58: "58mm",
  76: "76mm",
  112: "112mm",
};

// Matches PRINTER_WIDTHS in electron/print/escposRenderer.js so the browser and
// POS/Electron renderers agree on line length when a template omits startPosition.
const CHARS_PER_LINE = {
  80: 42,
  58: 32,
  76: 42,
  112: 64,
};

const CHAR_WIDTH_PX = 7.2;

function escapeHtml(str) {
  if (str === null || str === undefined) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function buildStyles(element) {
  const styles = [];
  const s = element.style || [];
  if (s.includes("bold") || element.bold) styles.push("font-weight:bold");
  if (s.includes("underline") || element.underline) styles.push("text-decoration:underline");
  if (s.includes("large") || element.large) styles.push("font-size:1.4em");
  if (s.includes("small") || element.small) styles.push("font-size:0.85em");

  const align = element.alignment || element.align || "left";
  styles.push(`text-align:${align}`);

  return styles.join(";");
}

/**
 * Render text elements with startPosition and addNewline support.
 * Elements with addNewline: false or startPosition are accumulated
 * into a single line before flushing.
 */
function renderTextElements(elements, startIdx, charsPerLine) {
  const lineItems = [];
  let i = startIdx;

  // Collect elements that belong on the same line
  while (i < elements.length && elements[i].type === "text") {
    const el = elements[i];
    lineItems.push(el);
    // If this element ends the line (addNewline !== false, or last element)
    if (el.addNewline !== false) {
      i++;
      break;
    }
    i++;
  }

  // If only one element with no startPosition, render simple div — a lone
  // right/center-aligned element already aligns correctly via text-align on
  // this block-level div, so it doesn't need a computed position.
  if (lineItems.length === 1 && lineItems[0].startPosition == null) {
    const el = lineItems[0];
    const style = buildStyles(el);
    const content = escapeHtml(el.content || "");
    return { html: `<div class="receipt-text" style="${style}">${content}</div>`, consumed: 1 };
  }

  // Multiple elements sharing a line — text-align has no effect on an inline
  // span, so a right/center-aligned element with no explicit startPosition
  // needs one computed from its own content length, or it collapses into
  // whatever precedes it.
  let html = `<div class="receipt-line-group" style="position:relative;white-space:pre;min-height:1.3em">`;
  for (const el of lineItems) {
    const style = buildStyles(el);
    const content = escapeHtml(el.content || "");
    let startPosition = el.startPosition;
    if (startPosition == null) {
      const align = el.alignment || el.align || "left";
      const rawLen = String(el.content || "").length;
      if (align === "right") {
        startPosition = Math.max(0, charsPerLine - rawLen);
      } else if (align === "center") {
        startPosition = Math.max(0, Math.floor((charsPerLine - rawLen) / 2));
      }
    }
    if (startPosition != null) {
      const left = startPosition * CHAR_WIDTH_PX;
      html += `<span style="position:absolute;left:${left}px;${style}">${content}</span>`;
    } else {
      html += `<span style="${style}">${content}</span>`;
    }
  }
  html += `</div>`;

  return { html, consumed: lineItems.length };
}

function renderLineElement() {
  return `<hr class="receipt-line" />`;
}

function renderSpacingElement(el) {
  const height = el.height || el.lines || 1;
  return `<div class="receipt-spacing" style="height:${height * 1.2}em"></div>`;
}

/**
 * Column widths, in px, for both header and data cells alike. Mirrors
 * calcColumnWidths() in electron/print/receiptUtils.js: when startPositions is
 * given, each column's width is the character gap to the next startPosition
 * (or to the end of the line); otherwise column.width is treated as a
 * proportional share of the line, not a raw CSS percentage. Same array is used
 * for every row so header and data cells can never disagree on column widths.
 */
function calcColumnWidthsPx(columns, startPositions, charsPerLine) {
  if (Array.isArray(startPositions) && startPositions.length === columns.length) {
    return startPositions.map((start, i) => {
      const end = i + 1 < startPositions.length ? startPositions[i + 1] : charsPerLine;
      return Math.max(1, end - start) * CHAR_WIDTH_PX;
    });
  }
  const totalShare = columns.reduce((s, c) => s + (c.width || Math.floor(100 / columns.length)), 0) || 1;
  return columns.map((c) => {
    const share = c.width || Math.floor(100 / columns.length);
    return Math.max(1, (share / totalShare) * charsPerLine) * CHAR_WIDTH_PX;
  });
}

function renderTableElement(el, charsPerLine) {
  const columns = el.columns || [];
  const rows = Array.isArray(el.data) ? el.data : [];

  if (columns.length === 0 || rows.length === 0) return "";

  const colWidthsPx = calcColumnWidthsPx(columns, el.startPositions, charsPerLine);

  let html = `<table class="receipt-table" style="table-layout:fixed">`;

  // Header row — use column.title (primary) or column.header (legacy)
  if (columns.some((c) => c.title || c.header)) {
    html += `<thead><tr>`;
    columns.forEach((col, i) => {
      const align = col.alignment || "left";
      const title = col.title || col.header || "";
      html += `<th style="text-align:${align};width:${colWidthsPx[i]}px">${escapeHtml(title)}</th>`;
    });
    html += `</tr>`;

    // Subheaders row (e.g. Arabic translations)
    if (Array.isArray(el.subheaders) && el.subheaders.length > 0) {
      html += `<tr class="receipt-subheader">`;
      for (let i = 0; i < columns.length; i++) {
        const col = columns[i];
        const align = col.alignment || "left";
        const subheader = el.subheaders[i] || "";
        html += `<th style="text-align:${align};width:${colWidthsPx[i]}px;font-weight:normal;font-style:italic">${escapeHtml(subheader)}</th>`;
      }
      html += `</tr>`;
    }

    // Subheader divider
    if (el.addSubheaderDivider) {
      html += `<tr><td colspan="${columns.length}" style="padding:0"><hr class="receipt-line" style="margin:2px 0" /></td></tr>`;
    }

    html += `</thead>`;
  }

  html += `<tbody>`;
  const colKeys = columns.map((col, idx) => {
    if (col.dataKey) return col.dataKey;
    const mapping = el.dataMapping || {};
    return Object.keys(mapping)[idx] || `col${idx}`;
  });

  for (const row of rows) {
    const rowStyle = row.style && row.style.includes("bold") ? 'style="font-weight:bold"' : "";
    html += `<tr ${rowStyle}>`;
    for (let i = 0; i < columns.length; i++) {
      const col = columns[i];
      const key = colKeys[i];
      const align = col.alignment || "left";
      const value = row[key] !== undefined && row[key] !== null ? row[key] : "";
      html += `<td style="text-align:${align};width:${colWidthsPx[i]}px">${escapeHtml(value)}</td>`;
    }
    html += `</tr>`;

    // Extra rows (e.g. discount lines below item)
    if (Array.isArray(row.extraRows)) {
      for (const extra of row.extraRows) {
        html += `<tr class="receipt-extra-row" style="font-size:0.9em">`;
        if (typeof extra === "string") {
          html += `<td colspan="${columns.length}" style="padding-left:8px">${escapeHtml(extra)}</td>`;
        } else if (Array.isArray(extra)) {
          for (let i = 0; i < columns.length; i++) {
            const align = columns[i]?.alignment || "left";
            html += `<td style="text-align:${align}">${escapeHtml(extra[i] || "")}</td>`;
          }
        } else if (extra && typeof extra === "object") {
          for (let i = 0; i < columns.length; i++) {
            const key = colKeys[i];
            const align = columns[i]?.alignment || "left";
            const val = extra[key] !== undefined ? extra[key] : "";
            html += `<td style="text-align:${align}">${escapeHtml(val)}</td>`;
          }
        }
        html += `</tr>`;
      }
    }
  }

  // Table-level extraRows
  if (Array.isArray(el.extraRows)) {
    for (const extra of el.extraRows) {
      html += `<tr class="receipt-extra-row" style="font-size:0.9em">`;
      if (typeof extra === "string") {
        html += `<td colspan="${columns.length}">${escapeHtml(extra)}</td>`;
      } else if (Array.isArray(extra)) {
        for (let i = 0; i < columns.length; i++) {
          const align = columns[i]?.alignment || "left";
          html += `<td style="text-align:${align}">${escapeHtml(extra[i] || "")}</td>`;
        }
      } else if (extra && typeof extra === "object") {
        for (let i = 0; i < columns.length; i++) {
          const key = colKeys[i];
          const align = columns[i]?.alignment || "left";
          const val = extra[key] !== undefined ? extra[key] : "";
          html += `<td style="text-align:${align}">${escapeHtml(val)}</td>`;
        }
      }
      html += `</tr>`;
    }
  }

  html += `</tbody></table>`;

  return html;
}

function renderBarcodeElement(el) {
  const content = escapeHtml(el.content || "");
  const barcodeType = el.barcodeType || el.format || "code128";
  return `<div class="receipt-barcode" style="text-align:center">
    <svg class="receipt-barcode-svg" data-value="${content}" data-format="${escapeHtml(barcodeType)}"></svg>
    <div style="text-align:center;font-size:0.8em">${content}</div>
  </div>`;
}

function renderQrCodeElement(el) {
  const content = el.content || "";
  if (!content) return "";
  const encodedContent = encodeURIComponent(content);
  return `<div class="receipt-qrcode" style="text-align:center;padding:8px">
    <img src="https://api.qrserver.com/v1/create-qr-code/?data=${encodedContent}&size=150x150" alt="QR Code" style="width:150px;height:150px" />
  </div>`;
}

function renderImageElement(el) {
  const src = el.src || el.url || "";
  const width = el.width || "100%";
  const align = el.alignment || el.align || "center";
  return `<div style="text-align:${align}"><img src="${escapeHtml(src)}" style="max-width:${width};height:auto" /></div>`;
}

/**
 * Convert processed template JSON to a full HTML document for printing.
 * @param {Object} processedTemplate - Output of processReceiptTemplate()
 * @param {number} paperWidth - Paper width in mm (default 80)
 * @returns {string} Complete HTML document string
 */
export function templateToHtml(processedTemplate, paperWidth = 80) {
  const width = PAPER_WIDTHS[paperWidth] || `${paperWidth}mm`;
  const charsPerLine = CHARS_PER_LINE[paperWidth] || Math.max(16, Math.round(paperWidth * 0.6));
  const elements = processedTemplate?.elements || [];

  const parts = [];
  let i = 0;
  while (i < elements.length) {
    const el = elements[i];
    switch (el.type) {
      case "text": {
        const result = renderTextElements(elements, i, charsPerLine);
        parts.push(result.html);
        i += result.consumed;
        break;
      }
      case "line":
      case "divider":
        parts.push(renderLineElement());
        i++;
        break;
      case "spacing":
        parts.push(renderSpacingElement(el));
        i++;
        break;
      case "table":
        parts.push(renderTableElement(el, charsPerLine));
        i++;
        break;
      case "barcode":
        parts.push(renderBarcodeElement(el));
        i++;
        break;
      case "qrcode":
        parts.push(renderQrCodeElement(el));
        i++;
        break;
      case "image":
        parts.push(renderImageElement(el));
        i++;
        break;
      default:
        i++;
        break;
    }
  }

  const body = parts.join("\n");

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
  @page {
    size: ${width} auto;
    margin: 0;
  }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    font-family: 'Courier New', Courier, monospace;
    font-size: 12px;
    line-height: 1.3;
    width: ${width};
    padding: 4mm 2mm;
    color: #000;
    -webkit-print-color-adjust: exact;
  }
  .receipt-text {
    padding: 1px 0;
    white-space: pre-wrap;
    word-break: break-word;
  }
  .receipt-line-group {
    padding: 1px 0;
  }
  .receipt-line {
    border: none;
    border-top: 1px dashed #000;
    margin: 4px 0;
  }
  .receipt-table {
    width: 100%;
    border-collapse: collapse;
    margin: 2px 0;
  }
  .receipt-table th {
    border-bottom: 1px solid #000;
    padding: 2px 1px;
    font-size: 0.9em;
  }
  .receipt-table td {
    padding: 1px;
    vertical-align: top;
    font-size: 0.95em;
  }
  .receipt-subheader th {
    border-bottom: none;
    font-size: 0.85em;
    padding-top: 0;
  }
  .receipt-extra-row td {
    padding-top: 0;
    color: #333;
  }
  .receipt-barcode {
    margin: 6px 0;
  }
</style>
</head>
<body>
${body}
</body>
</html>`;
}

export default templateToHtml;
